import React, { useContext, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaIdCard, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { mensagemDeErro, TAMANHO_MAXIMO_ARQUIVO } from '../../services/onboarding';
import { rastrearTeste, segundosDesde } from '../../services/rastreamentoTeste';
import { OnboardingContext } from '../../view/onboarding/OnboardingContext';

type ResultadoLeituraCnh = 'ok' | 'falha' | 'limite' | 'arquivo_recusado';

/** Classifica a falha da leitura para a telemetria, sem levar a mensagem (que pode citar o arquivo). */
const resultadoDoErro = (error: any): ResultadoLeituraCnh => {
    const resposta = error?.response;
    if (!resposta) return 'falha';
    if (resposta.status === 429) return 'limite';
    if (resposta.status === 413) return 'arquivo_recusado';
    if (resposta.status >= 500) return 'falha';
    const dados = resposta.data;
    const mensagem = String((typeof dados === 'string' ? dados : (dados?.mensagem || dados?.Mensagem)) ?? '');
    if (/leituras de CNH de hoje|limite/i.test(mensagem)) return 'limite';
    if (/envie (a foto|a CNH)|10 MB|grande/i.test(mensagem)) return 'arquivo_recusado';
    return 'falha';
};

/** Dados do titular lidos da CNH pela IA. O operador confere antes de salvar. */
export interface CnhLida {
    nome?: string;
    /** Só os números. */
    cpf?: string;
    rg?: string;
    /** Nº de registro da CNH, só os números. */
    numeroRegistro?: string;
    /** Como impressa na CNH: dd/MM/aaaa. */
    validade?: string;
}

export const lerCnh = (arquivo: File) => {
    const dados = new FormData();
    dados.append('arquivo', arquivo);
    return api.post<CnhLida>('/Locatario/LerCnh', dados, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 })
        .then(r => r.data);
};

export const formatarCpf = (cpf?: string) => {
    const d = (cpf ?? '').replace(/\D/g, '').slice(0, 11);
    return d.length === 11 ? d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : d;
};

/** "10/05/2030" -> "2030-05-10" (valor de campo de data). */
export const validadeCnhParaIso = (validade?: string) => {
    const m = (validade ?? '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : undefined;
};

const girar = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const Caixa = styled.div<{ $arrastando: boolean }>`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px 14px;
    padding: 12px 14px;
    border: 1px dashed ${p => (p.$arrastando ? '#1F467E' : '#93c5fd')};
    border-radius: 10px;
    background: ${p => (p.$arrastando ? '#dbeafe' : '#eff6ff')};
    transition: background 0.15s, border-color 0.15s;
`;

const Botao = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 16px;
    border: none;
    border-radius: 8px;
    background: #1F467E;
    color: #ffffff;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;

    &:disabled { opacity: 0.7; cursor: wait; }
    svg.girando { animation: ${girar} 1s linear infinite; }
`;

const Texto = styled.span`
    flex: 1;
    min-width: 200px;
    color: #1e3a5f;
    font-size: 13px;
    line-height: 1.4;
`;

interface Props {
    aoLer: (cnh: CnhLida) => void;
}

/**
 * Botão "Preencher pela CNH": o operador manda a foto ou o PDF da CNH e a IA devolve nome, CPF,
 * RG, registro e validade. Nada é gravado aqui; quem usa preenche o formulário para conferência.
 */
export default function BotaoLerCnh({ aoLer }: Props) {
    const entrada = useRef<HTMLInputElement>(null);
    const [lendo, setLendo] = useState(false);
    const [arrastando, setArrastando] = useState(false);
    // Dentro do onboarding o evento leva o prefixo dele; nas demais telas, "cnh_lida".
    const noOnboarding = useContext(OnboardingContext).ativo;
    const registrar = (resultado: ResultadoLeituraCnh, inicio: number) =>
        rastrearTeste(noOnboarding ? 'onboarding_cnh_lida' : 'cnh_lida', { resultado, segundos: segundosDesde(inicio) });

    const escolher = async (arquivo?: File | null) => {
        if (!arquivo) return;
        const inicio = Date.now();
        if (arquivo.size > TAMANHO_MAXIMO_ARQUIVO) {
            registrar('arquivo_recusado', inicio);
            toast.warning('Este arquivo passa de 10 MB. Envie uma foto menor da CNH ou preencha os dados à mão.');
            if (entrada.current) entrada.current.value = '';
            return;
        }
        setLendo(true);
        try {
            const cnh = await lerCnh(arquivo);
            registrar('ok', inicio);
            aoLer(cnh);
            toast.success('Dados da CNH preenchidos. Confira antes de salvar.');
        } catch (error: any) {
            registrar(resultadoDoErro(error), inicio);
            // Rede, tempo esgotado, 413 e 5xx viram texto claro; mensagem técnica do servidor não aparece.
            toast.error(mensagemDeErro(error, 'Não conseguimos ler esta CNH agora. Tente de novo ou preencha os dados à mão.'));
        } finally {
            setLendo(false);
            if (entrada.current) entrada.current.value = '';
        }
    };

    return (
        <Caixa
            $arrastando={arrastando}
            onDragOver={e => { e.preventDefault(); if (!lendo) setArrastando(true); }}
            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setArrastando(false); }}
            onDrop={e => { e.preventDefault(); setArrastando(false); if (!lendo) escolher(e.dataTransfer.files?.[0]); }}
        >
            <Botao type="button" disabled={lendo} onClick={() => entrada.current?.click()}>
                {lendo ? <FaSpinner className="girando" /> : <FaIdCard />}
                {lendo ? 'Lendo a CNH...' : 'Preencher pela CNH'}
            </Botao>
            <Texto>
                {arrastando ? 'Solte o arquivo para ler a CNH.' : 'Envie ou arraste aqui a foto ou o PDF da CNH do cliente. Preenchemos nome, CPF, RG e CNH para você conferir.'}
            </Texto>
            <input
                ref={entrada}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                hidden
                onChange={e => escolher(e.target.files?.[0])}
            />
        </Caixa>
    );
}
